/**
 * AuthContext.js - Authentication Context
 * 
 * Provides authentication state and functions throughout the app.
 * Uses React Context to avoid prop drilling.
 * 
 * UPDATED: Now works with async Supabase-backed dataStore.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import dataStore from '../data/dataStore';

// Create the context
const AuthContext = createContext(null);

/**
 * Get client information for audit logging
 */
async function getClientInfo() {
  const info = {
    browser: 'Unknown',
    os: 'Unknown',
    device: 'Unknown',
    ip: null,
    timestamp: new Date().toISOString()
  };
  
  // Parse user agent for browser/OS info
  const ua = navigator.userAgent;
  
  // Detect browser
  if (ua.includes('Firefox')) info.browser = 'Firefox';
  else if (ua.includes('Edg/')) info.browser = 'Edge';
  else if (ua.includes('Chrome')) info.browser = 'Chrome';
  else if (ua.includes('Safari')) info.browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR')) info.browser = 'Opera';
  
  // Detect OS
  if (ua.includes('Windows')) info.os = 'Windows';
  else if (ua.includes('Mac OS')) info.os = 'macOS';
  else if (ua.includes('Linux')) info.os = 'Linux';
  else if (ua.includes('Android')) info.os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) info.os = 'iOS';
  
  // Detect device type
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
    info.device = 'Mobile';
  } else if (ua.includes('iPad') || ua.includes('Tablet')) {
    info.device = 'Tablet';
  } else {
    info.device = 'Desktop';
  }
  
  // Try to get IP address (optional - may fail due to CORS)
  try {
    const response = await fetch('https://api.ipify.org?format=json', { timeout: 3000 });
    if (response.ok) {
      const data = await response.json();
      info.ip = data.ip;
    }
  } catch (e) {
    // IP fetch failed, continue without it
    console.log('Could not fetch IP address');
  }
  
  return info;
}

/**
 * AuthProvider component - wraps the app to provide auth state
 */
export function AuthProvider({ children }) {
  // Current logged-in user (null if not logged in)
  const [user, setUser] = useState(null);
  // Loading state while checking for existing session
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const storedUser = localStorage.getItem('cplc_current_user');
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          // Refresh user data from store in case it changed
          const freshUser = dataStore.getUserById(parsed.id);
          if (freshUser) {
            const { password: _, ...safeUser } = freshUser;
            setUser(safeUser);
          }
        } catch (error) {
          console.error('Error restoring session:', error);
          localStorage.removeItem('cplc_current_user');
        }
      }
      setLoading(false);
    };
    
    checkSession();
  }, []);


  /**
   * Login function (async for Supabase)
   * Returns a promise that resolves to { success, user } or { success, error }
   */
  const login = async (username, password) => {
    try {
      // Get client info for audit logging
      const clientInfo = await getClientInfo();
      
      const authenticatedUser = await dataStore.authenticate(username, password, clientInfo);
      if (authenticatedUser) {
        setUser(authenticatedUser);
        localStorage.setItem('cplc_current_user', JSON.stringify(authenticatedUser));
        return { success: true, user: authenticatedUser };
      }
      return { success: false, error: 'Invalid username or password' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'An error occurred during login' };
    }
  };

  /**
   * Signup function (async for Supabase)
   * Note: New signups don't have an "actor" since they're self-registering
   */
  const signup = async (username, password, name) => {
    try {
      const newUser = await dataStore.createUser({
        username,
        password,
        name,
        role: 'USER'
      }, null);
      
      const { password: _, ...safeUser } = newUser;
      setUser(safeUser);
      localStorage.setItem('cplc_current_user', JSON.stringify(safeUser));
      return { success: true, user: safeUser };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    if (user) {
      const clientInfo = await getClientInfo();
      await dataStore.logout(user, clientInfo);
    }
    setUser(null);
    localStorage.removeItem('cplc_current_user');
  };


  /**
   * Update current user (after profile changes)
   */
  const refreshUser = () => {
    if (user) {
      const freshUser = dataStore.getUserById(user.id);
      if (freshUser) {
        const { password: _, ...safeUser } = freshUser;
        setUser(safeUser);
        localStorage.setItem('cplc_current_user', JSON.stringify(safeUser));
      }
    }
  };

  // Value provided to consuming components
  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    refreshUser,
    isAdmin: user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN',
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
